import { useId, useRef } from "react";
import type { ReactNode, RefObject } from "react";
import { Alert } from "./Alert";
import { Button } from "./Button";
import { ComponentColor, ComponentSize } from "./types";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";

interface ConfirmDialogProps {
    title: ReactNode;
    // What confirming does, shown under the title.
    body: ReactNode;
    // Extra fields between the body and the buttons (a reason, say).
    children?: ReactNode;
    confirmLabel: string;
    confirmColor: ComponentColor;
    // The safe back-out's label. Defaults to "Go back".
    dismissLabel?: string;
    isProcessing: boolean;
    // Holds the confirm (e.g. until a required field is filled).
    confirmDisabled?: boolean;
    // The caller's failure copy; the dialog stays open to show it.
    errorMessage?: string | null;
    // False: a backdrop press keeps the dialog (typed input must survive it).
    closeOnBackdropClick?: boolean;
    // Focused on close when the opener is gone or disabled by then.
    returnFocusRef?: RefObject<HTMLElement | null>;
    onConfirm: () => void;
    onClose: () => void;
}

// The one confirm-dialog chassis: focus trap, scroll lock, Escape and backdrop
// via useDialogFocusTrap (#412/#427), and the safe back-out rendered first and
// marked data-dialog-initial-focus, so a stray Enter on open (or after
// processing ends) never confirms (#452).
export const ConfirmDialog = ({
    title,
    body,
    children,
    confirmLabel,
    confirmColor,
    dismissLabel = "Go back",
    isProcessing,
    confirmDisabled = false,
    errorMessage = null,
    closeOnBackdropClick = true,
    returnFocusRef,
    onConfirm,
    onClose,
}: ConfirmDialogProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing,
        onRequestClose: onClose,
        closeOnBackdropClick,
        returnFocusRef,
    });

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            {...backdropProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                {...panelProps}
                className="w-full max-w-md rounded-lg bg-base-100 p-6 text-neutral shadow-xl focus:outline-none"
                onClick={(event) => event.stopPropagation()}
            >
                <h2 id={titleId} className="mb-2 text-lg font-bold">
                    {title}
                </h2>
                <div className="mb-4 text-sm">{body}</div>

                {children}

                {errorMessage && (
                    <Alert color={ComponentColor.Error} className="mb-4">
                        {errorMessage}
                    </Alert>
                )}

                <div className="flex justify-end gap-2">
                    <span data-dialog-initial-focus="" className="contents">
                        <Button
                            color={ComponentColor.Neutral}
                            size={ComponentSize.SM}
                            disabled={isProcessing}
                            onClick={onClose}
                        >
                            {dismissLabel}
                        </Button>
                    </span>
                    <Button
                        color={confirmColor}
                        size={ComponentSize.SM}
                        loading={isProcessing}
                        disabled={confirmDisabled}
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};
